import {
  CheckoutOrderRefusalCode,
  CreateOrderPayload,
  CreateOrderResponse,
} from '../../../services/order-creation.service';
import { CheckoutRequestIds } from '../../../shared/utils/common-settings.util';

/**
 * CWS-1945: "Continue" no longer creates the order. The tablet keeps the order it would have
 * created as a draft, shows checkout from it, and creates (or reuses) the order on the server
 * only when the attendant pays — POST /order/checkout, which also claims the order so a second
 * tablet showing the same car cannot pay it too.
 */

/** Checkout include/exclude toggles the attendant changed on the summary of a draft. */
export type CheckoutSelectionScope = 'discount' | 'addon';

const REFUSAL_MESSAGES: Record<CheckoutOrderRefusalCode, string> = {
  ORDER_ALREADY_PAID: 'This order is already paid.',
  ORDER_ALREADY_COMPLETED: 'This order is already completed.',
  ORDER_CANCELLED: 'This order was cancelled. Close it and start a new sale.',
  PAYMENT_IN_PROGRESS: paymentInProgressMessage(null),
  CHECKOUT_ORDER_CHANGED:
    'This car already has an unpaid order for a different sale. Close this window and reopen the notification to review it.',
};

/** One id per checkout; retries reuse it so they pay the same order. */
export function newCheckoutRequestId(
  randomUUID: () => string = () => globalThis.crypto.randomUUID()
): string {
  return randomUUID();
}

/** CWS-1987: how long the server holds a claim (svc-order PAYMENT_ATTEMPT_STALE_MINUTES). */
export const CHECKOUT_CLAIM_TTL_MS = 10 * 60 * 1000;

/**
 * CWS-1987: the request id this tablet last claimed `orderId` with, while that claim still holds.
 * Reopening the order or reloading the app used to start a new id, and the server refused this
 * very tablet as "another device" until its own abandoned claim expired.
 */
export function rememberedCheckoutRequestId(
  ids: CheckoutRequestIds | undefined,
  orderId: string | null | undefined,
  now: number = Date.now()
): string | null {
  const entry = orderId ? ids?.[orderId] : undefined;
  return entry && typeof entry.requestId === 'string' && now - entry.at < CHECKOUT_CLAIM_TTL_MS
    ? entry.requestId
    : null;
}

/** CWS-1987: record a successful claim, dropping the ones that have expired on the server. */
export function rememberCheckoutRequestId(
  ids: CheckoutRequestIds | undefined,
  orderId: string,
  requestId: string,
  now: number = Date.now()
): CheckoutRequestIds {
  const live = Object.entries(ids ?? {}).filter(
    ([, entry]) => entry && now - entry.at < CHECKOUT_CLAIM_TTL_MS
  );
  return { ...Object.fromEntries(live), [orderId]: { requestId, at: now } };
}

/** CWS-1987: seconds until the claim that refused us expires, when the server sent it. */
export function checkoutRetryAfterSeconds(error: unknown): number | null {
  const body = (error as { error?: Record<string, any> } | null)?.error;
  const seconds = body?.['data']?.['details']?.['retry_after_seconds'];
  return typeof seconds === 'number' && Number.isFinite(seconds) && seconds >= 0 ? seconds : null;
}

function paymentInProgressMessage(retryAfterSeconds: number | null): string {
  const lead =
    'A payment for this order was already started (on another device, or earlier on this one). Finish or cancel it there';
  if (retryAfterSeconds === null) {
    return `${lead}, or try again within 10 minutes.`;
  }
  if (retryAfterSeconds === 0) {
    return `${lead}, or try again now.`;
  }
  const minutes = Math.ceil(retryAfterSeconds / 60);
  return `${lead}, or try again in about ${minutes} minute${minutes === 1 ? '' : 's'}.`;
}

/** The 409 code POST /order/checkout put in the error body, if any. */
export function checkoutRefusalCode(error: unknown): CheckoutOrderRefusalCode | null {
  const body = (error as { error?: Record<string, any> } | null)?.error;
  const code = body?.['data']?.['code'] ?? body?.['code'];
  return typeof code === 'string' && code in REFUSAL_MESSAGES
    ? (code as CheckoutOrderRefusalCode)
    : null;
}

/** What to tell the attendant when the pay press could not claim the order. */
export function checkoutClaimErrorMessage(error: unknown): string {
  const code = checkoutRefusalCode(error);
  if (code === 'PAYMENT_IN_PROGRESS') {
    return paymentInProgressMessage(checkoutRetryAfterSeconds(error));
  }
  if (code) {
    return REFUSAL_MESSAGES[code];
  }
  const body = (error as { error?: Record<string, any> } | null)?.error;
  const serverMessage = body?.['message'];
  return typeof serverMessage === 'string' && serverMessage.trim()
    ? serverMessage
    : 'Could not start the payment. Please try again.';
}

/** Refusals after which paying again from this window can never succeed. */
export function isFinalCheckoutRefusal(error: unknown): boolean {
  const code = checkoutRefusalCode(error);
  return (
    code === 'ORDER_ALREADY_PAID' ||
    code === 'ORDER_ALREADY_COMPLETED' ||
    code === 'ORDER_CANCELLED'
  );
}

/**
 * The create-order response checkout is built from, for a draft that has no order yet. Same
 * shape the old POST /order answer had, without the server-side ids.
 */
export function buildCheckoutDraftResponse(payload: CreateOrderPayload): CreateOrderResponse {
  return {
    status: true,
    status_code: 200,
    message: 'Checkout draft',
    data: {
      order: {
        id: '',
        order_id: '',
        customer_id: payload.customer_id,
        user_id: '',
        package_id: payload.package_id ?? '',
        location_id: payload.location_id,
        vehicle_id: payload.vehicle_id ?? '',
        wash_type: payload.wash_type,
        list_price: payload.list_price != null ? String(payload.list_price) : '',
        discount: payload.discount != null ? String(payload.discount) : '0',
        final_price: String(payload.final_price ?? ''),
        appointment_time: payload.appointment_time,
        source: payload.source,
        order_status: 'pending',
        payment_status: 'pending',
        status: true,
        created_at: '',
        updated_at: '',
        discount_id: payload.discount_id ?? undefined,
      },
    },
  } as CreateOrderResponse;
}

/**
 * The draft to send when the attendant pays: the payload Continue built, with the summary
 * toggles they changed since (and only those, so an untouched toggle never strips a discount or
 * add-on the form had selected).
 */
export function applyCheckoutSelectionToDraft(
  draft: CreateOrderPayload,
  touched: ReadonlySet<CheckoutSelectionScope>,
  selection: {
    finalPrice: string | number;
    listPrice: string | number | null;
    discount?: { amount: number; discountId: string | null };
    addOnVariants?: CreateOrderPayload['add_on_variants'];
  }
): CreateOrderPayload {
  if (touched.size === 0) {
    return { ...draft };
  }
  const next: CreateOrderPayload = {
    ...draft,
    final_price: selection.finalPrice,
    ...(selection.listPrice != null ? { list_price: selection.listPrice } : {}),
  };
  if (touched.has('discount') && selection.discount) {
    next.discount = selection.discount.amount;
    if (selection.discount.discountId) {
      next.discount_id = selection.discount.discountId;
    } else {
      delete next.discount_id;
    }
  }
  if (touched.has('addon') && selection.addOnVariants) {
    next.add_on_variants = selection.addOnVariants;
  }
  return next;
}
